import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { ArrowUpDown } from 'lucide-react';
import { useMemo, useState } from 'react';

export interface AnalyticsColumn<T> {
  key: keyof T | string;
  label: string;
  sortable?: boolean;
  align?: 'left' | 'right' | 'center';
  render?: (row: T) => React.ReactNode;
}

interface AnalyticsTableProps<T> {
  columns: AnalyticsColumn<T>[];
  rows: T[];
  /** Called when a row is clicked — used for drill-down */
  onRowClick?: (row: T) => void;
  /** Key used to highlight the active row */
  activeRowKey?: string | null;
  /** Function to get the row's unique string key */
  getRowKey: (row: T) => string;
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
}

type SortDir = 'asc' | 'desc';

function getNestedValue<T>(obj: T, key: string): unknown {
  return (obj as Record<string, unknown>)[key];
}

export function AnalyticsTable<T>({
  columns,
  rows,
  onRowClick,
  activeRowKey,
  getRowKey,
  isLoading,
  emptyMessage = 'No data available.',
  className,
}: AnalyticsTableProps<T>) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedRows = useMemo(
    () =>
      sortKey
        ? [...rows].sort((a, b) => {
            const av = getNestedValue(a, sortKey);
            const bv = getNestedValue(b, sortKey);
            const aNum = typeof av === 'number' ? av : 0;
            const bNum = typeof bv === 'number' ? bv : 0;
            return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
          })
        : rows,
    [rows, sortKey, sortDir]
  );

  return (
    <div className={cn('rounded-md border', className)}>
      <Table>
        <TableHeader>
          <TableRow>
            {columns.map((col) => (
              <TableHead
                key={String(col.key)}
                className={cn(
                  col.align === 'right' && 'text-right',
                  col.align === 'center' && 'text-center',
                  col.sortable && 'cursor-pointer select-none hover:bg-muted/50'
                )}
                onClick={col.sortable ? () => handleSort(String(col.key)) : undefined}
              >
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {col.sortable && <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
                </span>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center py-8 text-muted-foreground"
              >
                Loading…
              </TableCell>
            </TableRow>
          ) : sortedRows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="text-center py-8 text-muted-foreground"
              >
                {emptyMessage}
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row) => {
              const key = getRowKey(row);
              const isActive = key === activeRowKey;
              return (
                <TableRow
                  key={key}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  className={cn(
                    onRowClick && 'cursor-pointer hover:bg-muted/50',
                    isActive && 'bg-muted'
                  )}
                >
                  {columns.map((col) => (
                    <TableCell
                      key={String(col.key)}
                      className={cn(
                        col.align === 'right' && 'text-right',
                        col.align === 'center' && 'text-center'
                      )}
                    >
                      {col.render
                        ? col.render(row)
                        : String(getNestedValue(row, String(col.key)) ?? '')}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </div>
  );
}
